import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../../components/layout/OnboardingLayout";
import MovieSelectCard from "../../components/cards/MovieSelectCard";
import Button from "../../components/buttons/Button";
import "../../components/styles/personalizePage.scss";

/**
 * PersonalizePage — "Let's personalize your experiences" (Onboarding Step 5)
 * Figma: 1.6 Onboarding(Desktop) (24654:6853)
 *
 * Stepper: currentStep=3 (Setting an account — active, same as profiles)
 * Content (864px wide):
 *   - Back arrow (36x36)
 *   - Title: "Let's personalize your experiences" (36px/600 #fafafa)
 *   - Subtitle: "Select the films or series you like..." (20px/400 #a3a3a3)
 *   - Movie grid: flex-wrap, gap-y 32px, justify-between
 *     - 3 columns of 267x353px MovieSelectCards
 *   - Sticky bottom bar: backdrop-blur, 104px, "Continue" button (14px/600)
 */

// Placeholder movies — replace with API data
const SAMPLE_MOVIES = [
  { id: 1, title: "Breaking Bad" },
  { id: 2, title: "Stranger Things" },
  { id: 3, title: "The Crown" },
  { id: 4, title: "Money Heist" },
  { id: 5, title: "Squid Game" },
  { id: 6, title: "Dark" },
  { id: 7, title: "Narcos" },
  { id: 8, title: "Peaky Blinders" },
  { id: 9, title: "The Witcher" },
  { id: 10, title: "Ozark" },
  { id: 11, title: "Lupin" },
  { id: 12, title: "Black Mirror" },
  { id: 13, title: "The Queen's Gambit" },
  { id: 14, title: "Bridgerton" },
  { id: 15, title: "Extraction" },
  { id: 16, title: "Enola Holmes" },
];

const PersonalizePage = () => {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState(new Set([1, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]));
  const [isLoading, setIsLoading] = useState(false);

  const toggleMovie = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleContinue = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);

    // TODO: Replace with actual preference save API call
    setTimeout(() => {
      setIsLoading(false);
      navigate("/onboarding/loading");
    }, 1000);
  }, [isLoading, navigate]);

  return (
    <OnboardingLayout
      currentStep={3}
      title="Let's personalize your experiences"
      subtitle="Select the films or series you like to help us recommend them for you"
      contentWidth={864}
      showFooter={false}
      bottomBar={
        <Button
          label="Continue"
          size="sm"
          variant="primary"
          isLoading={isLoading}
          action={handleContinue}
          className="mango-btn--auto-width"
        />
      }
    >
      {/* Movie Grid — Figma: flex-wrap, gap-y 32px, justify-between, 864px */}
      <div className="personalize__grid">
        {SAMPLE_MOVIES.map((movie) => (
          <MovieSelectCard
            key={movie.id}
            title={movie.title}
            poster={movie.poster}
            selected={selectedIds.has(movie.id)}
            onClick={() => toggleMovie(movie.id)}
          />
        ))}
      </div>
    </OnboardingLayout>
  );
};

export default PersonalizePage;
